import { DateTime } from '@mode-switch/express';
import {
  DatePart,
  EncRelativeDateFilter,
  EncRelativeDateFilterField,
  FilterType,
  RelativeDateFilterHelpers,
} from '@mode-vegas/vegas';
import { CompiledField, FormulaOperations } from '@mode/shared/util-switch';
import { formatInTimeZone } from 'date-fns-tz';
import { isEqual } from 'lodash';
import { IOpt as MenuItem } from '../helpers/field-helpers';

const today = new Date();

export const enum Period {
  Custom,
  Fixed,
  Today,
  Yesterday,
  Last7Days,
  Previous2Weeks,
  Previous1Month,
  Previous2Months,
  Previous1Quarter,
  Previous2Quarters,
  MonthToDate,
  YearToDate,
}

export type PeriodOption = MenuItem<Period>;
export const periodOptions = getPeriodOptions();

export function getPeriodOptions(
  anchor = today,
  weekStart?: WeekStart,
  yearStart?: YearStart
): ReadonlyArray<PeriodOption> {
  return [
    {
      name: 'Custom',
      value: Period.Custom,
    },
    {
      name: 'Fixed date range',
      value: Period.Fixed,
    },
    {
      name: 'Today',
      value: Period.Today,
      desc: getMenuDescDisplayRange(Period.Today, anchor, weekStart, yearStart),
    },
    {
      name: 'Yesterday',
      value: Period.Yesterday,
      desc: getMenuDescDisplayRange(Period.Yesterday, anchor, weekStart, yearStart),
    },
    {
      name: 'Last 7 days',
      value: Period.Last7Days,
      desc: getMenuDescDisplayRange(Period.Last7Days, anchor, weekStart, yearStart),
      divider: true,
    },
    {
      name: 'Previous 2 weeks',
      value: Period.Previous2Weeks,
      desc: getMenuDescDisplayRange(Period.Previous2Weeks, anchor, weekStart, yearStart),
    },
    {
      name: 'Previous 1 month',
      value: Period.Previous1Month,
      desc: getMenuDescDisplayRange(Period.Previous1Month, anchor, weekStart, yearStart),
    },
    {
      name: 'Previous 2 months',
      value: Period.Previous2Months,
      desc: getMenuDescDisplayRange(Period.Previous2Months, anchor, weekStart, yearStart),
    },
    {
      name: 'Previous 1 quarter',
      value: Period.Previous1Quarter,
      desc: getMenuDescDisplayRange(Period.Previous1Quarter, anchor, weekStart, yearStart),
    },
    {
      name: 'Previous 2 quarters',
      value: Period.Previous2Quarters,
      desc: getMenuDescDisplayRange(Period.Previous2Quarters, anchor, weekStart, yearStart),
      divider: true,
    },
    {
      name: 'Month-to-date',
      value: Period.MonthToDate,
      desc: getMenuDescDisplayRange(Period.MonthToDate, anchor, weekStart, yearStart),
    },
    {
      name: 'Year-to-date',
      value: Period.YearToDate,
      desc: getMenuDescDisplayRange(Period.YearToDate, anchor, weekStart, yearStart),
    },
  ];
}

/** @internal for generating the Period menu descriptions */
function getMenuDescDisplayRange(period: Period, anchor: Date, weekStart?: WeekStart, yearStart?: YearStart): string {
  const { from, to } = getPartialFilterConfigFromPeriod(period);

  const fromDate = RelativeDateFilterHelpers.getDate(anchor, from, weekStart, yearStart);
  const toDate = RelativeDateFilterHelpers.getDate(anchor, to, weekStart, yearStart);

  if (fromDate === undefined || toDate === undefined) {
    throw new Error(`getMenuDescDisplayRange: fromDate and toDate must be defined`);
  }

  const fromStr = formatInTimeZone(fromDate, 'Z', `MMM d',' yyyy`);
  const toStr = formatInTimeZone(toDate, 'Z', `MMM d',' yyyy`);

  return `${fromStr} — ${toStr}`;
}

export const enum RelRange {
  StartOfPrevious,
  StartOfThis,
  StartOfNext,
  EndOfPrevious,
  EndOfThis,
  EndOfNext,
  Fixed,
  Unset,
}

export type RangeOption = MenuItem<RelRange>;
export const rangeOptions: ReadonlyArray<RangeOption> = [
  { name: 'Start of Previous', value: RelRange.StartOfPrevious },
  { name: 'Start of This', value: RelRange.StartOfThis },
  { name: 'Start of Next', value: RelRange.StartOfNext, divider: true },
  { name: 'End of Previous', value: RelRange.EndOfPrevious },
  { name: 'End of This', value: RelRange.EndOfThis },
  { name: 'End of Next', value: RelRange.EndOfNext, divider: true },
  { name: 'Fixed date', value: RelRange.Fixed },
  { name: 'Unset', value: RelRange.Unset },
];

export type DatePartOption = MenuItem<DatePart>;
export const datePartOptions: ReadonlyArray<DatePartOption> = [
  { value: DatePart.DAY, name: 'day(s)', desc: '12:00am-11:59pm UTC' },
  { value: DatePart.WEEK, name: 'week(s)', desc: 'custom starting day' },
  { value: DatePart.MONTH, name: 'month(s)', desc: 'Jan, Feb, Mar, etc' },
  { value: DatePart.QUARTER, name: 'quarter(s)', desc: 'custom starting month' },
  { value: DatePart.YEAR, name: 'year(s)', desc: 'Jan-Dec' },
];

export const enum WeekStart {
  Sun = 0,
  Mon,
  Tue,
  Wed,
  Thu,
  Fri,
  Sat,
}

export type WeekStartOption = MenuItem<WeekStart>;
export const weekStartOptions: ReadonlyArray<WeekStartOption> = [
  { name: 'Sunday', value: WeekStart.Sun },
  { name: 'Monday', value: WeekStart.Mon },
  { name: 'Tuesday', value: WeekStart.Tue },
  { name: 'Wednesday', value: WeekStart.Wed },
  { name: 'Thursday', value: WeekStart.Thu },
  { name: 'Friday', value: WeekStart.Fri },
  { name: 'Saturday', value: WeekStart.Sat },
];

export const enum YearStart {
  Jan = 1,
  Feb,
  Mar,
  Apr,
  May,
  Jun,
  Jul,
  Aug,
  Sep,
  Oct,
  Nov,
  Dec,
}

export type YearStartOption = MenuItem<YearStart>;
export const yearStartOptions: ReadonlyArray<YearStartOption> = [
  { name: 'January', value: YearStart.Jan },
  { name: 'February', value: YearStart.Feb },
  { name: 'March', value: YearStart.Mar },
  { name: 'April', value: YearStart.Apr },
  { name: 'May', value: YearStart.May },
  { name: 'June', value: YearStart.Jun },
  { name: 'July', value: YearStart.Jul },
  { name: 'August', value: YearStart.Aug },
  { name: 'September', value: YearStart.Sep },
  { name: 'October', value: YearStart.Oct },
  { name: 'November', value: YearStart.Nov },
  { name: 'December', value: YearStart.Dec },
];

export function getPartialFilterConfigFromPeriod(period: Period): {
  from: EncRelativeDateFilter.FromTo;
  to: EncRelativeDateFilter.FromTo;
} {
  switch (period) {
    case Period.Custom:
      return {
        from: { type: 'unset' },
        to: { type: 'unset' },
      };
    case Period.Fixed:
      return {
        from: {
          type: 'fixed',
          value: NaN,
        },
        to: {
          type: 'fixed',
          value: NaN,
        },
      };
    case Period.Today:
      return {
        from: {
          type: 'relative',
          clamp: 'start',
          relTo: 'current',
          part: DatePart.DAY,
          value: 1,
        },
        to: {
          type: 'relative',
          clamp: 'end',
          relTo: 'current',
          part: DatePart.DAY,
          value: 1,
        },
      };
    case Period.Yesterday:
      return {
        from: {
          type: 'relative',
          clamp: 'start',
          relTo: 'previous',
          part: DatePart.DAY,
          value: 1,
        },
        to: {
          type: 'relative',
          clamp: 'end',
          relTo: 'previous',
          part: DatePart.DAY,
          value: 1,
        },
      };
    case Period.Last7Days:
      return {
        from: {
          type: 'relative',
          clamp: 'start',
          relTo: 'previous',
          part: DatePart.DAY,
          value: 7,
        },
        to: {
          type: 'relative',
          clamp: 'end',
          relTo: 'previous',
          part: DatePart.DAY,
          value: 1,
        },
      };
    case Period.Previous2Weeks:
      return {
        from: {
          type: 'relative',
          clamp: 'start',
          relTo: 'previous',
          part: DatePart.WEEK,
          value: 2,
        },
        to: {
          type: 'relative',
          clamp: 'end',
          relTo: 'previous',
          part: DatePart.WEEK,
          value: 1,
        },
      };
    case Period.Previous1Month:
      return {
        from: {
          type: 'relative',
          clamp: 'start',
          relTo: 'previous',
          part: DatePart.MONTH,
          value: 1,
        },
        to: {
          type: 'relative',
          clamp: 'end',
          relTo: 'previous',
          part: DatePart.MONTH,
          value: 1,
        },
      };
    case Period.Previous2Months:
      return {
        from: {
          type: 'relative',
          clamp: 'start',
          relTo: 'previous',
          part: DatePart.MONTH,
          value: 2,
        },
        to: {
          type: 'relative',
          clamp: 'end',
          relTo: 'previous',
          part: DatePart.MONTH,
          value: 1,
        },
      };
    case Period.Previous1Quarter:
      return {
        from: {
          type: 'relative',
          clamp: 'start',
          relTo: 'previous',
          part: DatePart.QUARTER,
          value: 1,
        },
        to: {
          type: 'relative',
          clamp: 'end',
          relTo: 'previous',
          part: DatePart.QUARTER,
          value: 1,
        },
      };
    case Period.Previous2Quarters:
      return {
        from: {
          type: 'relative',
          clamp: 'start',
          relTo: 'previous',
          part: DatePart.QUARTER,
          value: 2,
        },
        to: {
          type: 'relative',
          clamp: 'end',
          relTo: 'previous',
          part: DatePart.QUARTER,
          value: 1,
        },
      };
    case Period.MonthToDate:
      return {
        from: {
          type: 'relative',
          clamp: 'start',
          relTo: 'current',
          part: DatePart.MONTH,
          value: 1,
        },
        to: {
          type: 'relative',
          clamp: 'end',
          relTo: 'current',
          part: DatePart.DAY,
          value: 1,
        },
      };
    case Period.YearToDate:
      return {
        from: {
          type: 'relative',
          clamp: 'start',
          relTo: 'current',
          part: DatePart.YEAR,
          value: 1,
        },
        to: {
          type: 'relative',
          clamp: 'end',
          relTo: 'current',
          part: DatePart.DAY,
          value: 1,
        },
      };
  }
}

export function getPeriod(filter?: EncRelativeDateFilter): Period {
  if (filter?.filterType === FilterType.RelativeDate) {
    if (filter.from.type === 'fixed' && filter.to.type === 'fixed') {
      return Period.Fixed;
    }

    for (const { value } of periodOptions) {
      if (value === Period.Custom) {
        continue;
      }

      const config = getPartialFilterConfigFromPeriod(value);
      if (
        isEqual(config, {
          from: filter.from,
          to: filter.to,
        })
      ) {
        return value;
      }
    }
  }

  return Period.Custom;
}

export function getDatePart(fromto?: EncRelativeDateFilter.FromTo): DatePart | undefined {
  if (fromto?.type === 'relative') {
    return fromto.part;
  }
}

export function getRelativeRangePart(fromto: EncRelativeDateFilter.FromTo): RelRange {
  if (fromto.type === 'unset') {
    return RelRange.Unset;
  }

  if (fromto.type === 'fixed') {
    return RelRange.Fixed;
  }

  // StartOfPrevious
  // StartOfThis
  // StartOfNext
  if (fromto.clamp === 'start') {
    switch (fromto.relTo) {
      case 'previous':
        return RelRange.StartOfPrevious;
      case 'current':
        return RelRange.StartOfThis;
      case 'next':
        return RelRange.StartOfNext;
    }
  }

  // EndOfPrevious
  // EndOfThis
  // EndOfNext
  switch (fromto.relTo) {
    case 'previous':
      return RelRange.EndOfPrevious;
    case 'current':
      return RelRange.EndOfThis;
    case 'next':
      return RelRange.EndOfNext;
  }
}

export function makeFilterFromTo(
  range: RelRange,
  part: DatePart,
  value: number | null,
  fixed: number | null
): EncRelativeDateFilter.FromTo {
  switch (range) {
    case RelRange.Unset:
      return {
        type: 'unset',
      };
    case RelRange.Fixed:
      expectDate(fixed);
      return {
        type: 'fixed',
        value: fixed,
      };
    case RelRange.StartOfPrevious:
      expectDatePart(part);
      expectInt(value);
      return {
        type: 'relative',
        clamp: 'start',
        relTo: 'previous',
        part,
        value,
      };
    case RelRange.StartOfThis:
      expectDatePart(part);
      expectInt(value);
      return {
        type: 'relative',
        clamp: 'start',
        relTo: 'current',
        part,
        value,
      };
    case RelRange.StartOfNext:
      expectDatePart(part);
      expectInt(value);
      return {
        type: 'relative',
        clamp: 'start',
        relTo: 'next',
        part,
        value,
      };
    case RelRange.EndOfPrevious:
      expectDatePart(part);
      expectInt(value);
      return {
        type: 'relative',
        clamp: 'end',
        relTo: 'previous',
        part,
        value,
      };
    case RelRange.EndOfThis:
      expectDatePart(part);
      expectInt(value);
      return {
        type: 'relative',
        clamp: 'end',
        relTo: 'current',
        part,
        value,
      };
    case RelRange.EndOfNext:
      expectDatePart(part);
      expectInt(value);
      return {
        type: 'relative',
        clamp: 'end',
        relTo: 'next',
        part,
        value,
      };
  }
}

function isValidInt(value: unknown): value is number {
  if (Number.isSafeInteger(value)) {
    return (value as number) > 0;
  }
  return false;
}

function isValidDate(value: unknown): value is number {
  if (Number.isSafeInteger(value)) {
    return Number.isFinite(+new Date(value as number));
  }
  return false;
}

function expectInt(value: unknown): asserts value is number {
  if (!isValidInt(value)) {
    throw new Error(`Expected EncRelativeDateIntervalPart value to be an integer >= 1, saw "${value}"`);
  }
}

function expectDate(value: unknown): asserts value is number {
  if (!isValidDate(value)) {
    throw new Error(`Expected fixed date value to be a valid date, saw "${value}"`);
  }
}

function expectDatePart(value?: unknown): asserts value is DatePart {
  if (value === undefined) {
    throw new Error(`Expected EncRelativeDateIntervalPart DatePart unit to be defined`);
  }
}

/**
 * Returns a new EncRelativeDateFilterField with its week and year start config
 * derived from the filter _then_ the filter formula
 */
export function relativeDateFilterSourcedByFilter(
  filter: EncRelativeDateFilterField,
  field: CompiledField
): EncRelativeDateFilterField {
  return relativeDateFilterSourcedBy(filter, field, 'filter');
}

/**
 * Returns a new EncRelativeDateFilterField with its week and year start config
 * derived from the field _then_ the filter itself
 */
export function relativeDateFilterSourcedByField(
  filter: EncRelativeDateFilterField,
  field: CompiledField
): EncRelativeDateFilterField {
  return relativeDateFilterSourcedBy(filter, field, 'field');
}

/**
 * @private
 * Returns a new EncRelativeDateFilterField with its week and year start config
 * derived from the filter or filter field as specified in "by", falling back
 * to the opposite source when the "by" config isn't present. For example:
 *
 *    field formula: datetrunc('day', date, 'monday')
 * filter weekStart: tuesday
 *
 *  source by "field": weekstart is "monday"
 * source by "filter": weekstart is "tuesday"
 *
 *    field formula: datetrunc('day', date)
 * filter weekStart: tuesday
 *
 *  source by "field": weekstart is "tuesday"
 * source by "filter": weekstart is "tuesday"
 */
function relativeDateFilterSourcedBy(
  filter: EncRelativeDateFilterField,
  field: CompiledField,
  by: 'field' | 'filter'
): EncRelativeDateFilterField {
  const source = (() => {
    const weekStart = DateTime.weekStart(
      by === 'field'
        ? // by field→filter
          (field.weekStart ?? filter.weekStart)
        : // by filter→field
          (filter.weekStart ?? field.weekStart)
    );

    const yearStart = DateTime.yearStart(
      by === 'field'
        ? // by field→filter
          (field.yearStart ?? filter.yearStart)
        : // by filter→field
          (filter.yearStart ?? field.yearStart)
    );

    // Do not aggregate relative date filters to a unit coarser than DAY,
    // it makes reasoning about what the filter is doing _very difficult_
    if (filter.from.type === 'relative' || filter.to.type === 'relative') {
      return FormulaOperations.dateTrunc(field, DateTime.Unit.Day, weekStart, yearStart);
    }
    // If there's a date function applied just make sure it's a datetrunc
    else if (field.dateFuncUnit) {
      return FormulaOperations.dateTrunc(field, field.dateFuncUnit, weekStart, yearStart);
    }
    // No change
    else {
      return field.formula;
    }
  })();

  // Re-source the field
  const newField = field.withNewFormula(source);

  return {
    ...filter,
    formula: newField.formula,
    // Will be "undefined" when the formula itself does not include w/y start
    // values. This ensures we keep the filter config in sync with the formula
    weekStart: DateTime.weekStartDow(newField.weekStart),
    yearStart: DateTime.yearStartMonth(newField.yearStart),
  };
}
